import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Spin, Tooltip } from 'antd';
import { CalendarClock } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { dutyPointDisplayTitle, readableDate } from '@pams/shared';
import type { ArrangementDto, DeleteImpactDto, DutyPointDto, PublishPreviewDto, SharedColumnDto } from '@pams/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/PageHeader';
import { templateHref } from '@/lib/templateLinks';
import { homeListOf, listOriginOf, originForOrder, withRecordOrigin } from '@/lib/listOrigin';
import { StatusPill } from '@/components/StatusPill';
import { StatusDot } from '@/components/StatusDot';
import { RaiserPill } from '@/components/RaiserPill';
import { EmptyState } from '@/components/EmptyState';
import { CommandContacts } from '@/components/preview/CommandContacts';
import { awaitedIsLive } from '@/lib/commandWords';
import { SharedColumnLines } from '@/components/preview/SharedColumnLines';
import { ShortfallNotes } from '@/components/preview/ShortfallNotes';
import { coveredElsewhere } from '@/lib/sharedColumns';
import { DutyRoster } from '@/components/fill/DutyRoster';
import { PREVIEW_COPY } from '@/components/preview/copy';
import { AttachmentsMenu } from '@/components/attachments/AttachmentsMenu';
import { UnpublishAction } from '@/components/arrangement/UnpublishAction';
import { RevertRunningAction } from '@/components/arrangement/RevertRunningAction';
import { DeleteRevertedDraftAction } from '@/components/arrangement/DeleteRevertedDraftAction';
import { ARCHIVE_COPY } from '@/components/archive/copy';
import { useArchiveArrangement } from '@/components/archive/useArchiveArrangement';
import { useAuth } from '@/hooks/useAuth';
import { useArrangementAuthoringSetting } from '@/hooks/useArrangementAuthoringSetting';
import {
  READ_ONLY_TOOLTIP,
  can,
  mayArchiveTier,
  mayAuthorArrangement,
  mayUseTemplates,
  actingOrgUnitTypeOf,
} from '@/lib/accessRole';
import { istDateTime } from '@/lib/analyticsReport';
import { arrangementTypeLabel, templateSourceLabel } from '@/lib/labels';
import { fillSummary, orderStatusWord, pointOutstanding, presenceLine } from '@/lib/statusPresentation';
import { queryKeys } from '@/lib/queryKeys';
import { api, type ApiError } from '@/lib/api';
import { ARRANGEMENTS_COPY } from '../Arrangements/copy';
import { VIEW_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C3a §4] THE ARRANGEMENT, READ — FULL WIDTH, FOUR CONTEXTUAL ACTIONS, ONE CARD PER PLACE.
 *
 *  ⚠️ IT READS `?view=preview`, AND THAT IS §4f SATISFIED BY CONSTRUCTION RATHER THAN BY CARE.
 *  `enrichForPreview` returns `{...dto, dutyPoints, required, presence}` — `dto` is `toDto`'s
 *  UNMODIFIED value, so the preview view is a strict SUPERSET of `?view=record`, gated on the
 *  same `arrangement.read`, adding no aggregation and no FR-3.6 read site. `?view=record` cannot
 *  do this page at all: no `preview.duty.deploymentId` (so no `Name officers` target), no
 *  `posts[]` (so no force grid), no per-card date or time. Reading BOTH would be worse than
 *  either — two cache entries under `detail(id)` that can disagree, which is §4f's own objection.
 *
 *  ⚠️ AND IT COMPUTES NO COUNT OF ITS OWN. Every figure on this page is a field; `grep` over this
 *  file finds no arithmetic on a fill number. That is the whole of §4f: a fourth renderer that
 *  derived its own numbers would drift from the other three.
 *
 *  ─── ⚠️ §4a — THE ACTION SET IS CONTEXTUAL, AND THE TIER IS FETCHED WITH THE RECORD ───
 *
 *  "Do not render an action that will refuse" is unsatisfiable without knowing the archive TIER
 *  before the click, so this page pays one `GET /:id/delete-impact` per mount of an unarchived
 *  order. **THAT REVERSES [C1e §6b]**, which chose the `Archive` rename precisely to avoid it —
 *  and the reversal is here rather than silent. It is one request over an index prefix that
 *  already exists, and both impact reads are `enabled`-gated so neither is paid where it cannot
 *  apply.
 *
 *  ⚠️ THE LIST DOES THE OPPOSITE AND IS ALSO RIGHT. `pages/Arrangements` resolves the tier ON
 *  CLICK, because one request per record is twenty-five per page there. Stated at both sites so
 *  the next reader does not read them as inconsistent.
 *
 *  ⚠️ AND THE BLOCKERS MODAL IS NOT DUPLICATED. The to-do list for a blocked publish IS the form,
 *  so a blocked Publish navigates to `/edit` rather than opening a second dialog that would be a
 *  second place the same warnings are worded. `publishControlState` is deliberately NOT imported:
 *  its `published` arm means "go to the record", which is this page.
 *
 *  ⚠️ §4b — `Start a copy` AND `Update duties` DO NOT EXIST IN THIS FILE. No `duplicate` mutation,
 *  no `restamp` mutation. The LIST's `Start from a previous one` is a different page and stays.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [C3a §4d] A `<details>`, NOT AN ANTD `Collapse` AND NOT RADIX — AND THAT IS FORCED.
 *
 * rc-collapse and Radix Accordion both UNMOUNT closed content, which is exactly what
 * [UX-FINAL]'s "ONE DOM COPY — a class, never `hidden`" was written against: a closed card would
 * disappear from every assertion that addresses it. `<details>` keeps the body in the DOM with no
 * cascade to resolve, which is the one form of this jsdom can see. It is also invisible to
 * `census.spec.ts`'s six-primitive scan, so it costs no `overlays.ts` bookkeeping.
 *
 * ⚠️ THE PRECEDENT ALREADY EXISTS — `components/preview/ArrangementPreviewModal.tsx`'s own duty
 * point card and `pwa/src/components/DutySection.tsx` are both this shape.
 *
 * ⚠️ AND `Name officers` IS A REAL `<a>` INSIDE THE `<summary>`, WITH `stopPropagation`. An
 * anchor is `NameCell`'s rule — ⌘-click and copy-link must work — and without the guard,
 * activating it would also toggle the disclosure it sits in.
 */
const DutyPointCard = ({
  point,
  open,
  shared,
  ordinalOf,
  live,
}: {
  point: DutyPointDto;
  open: boolean;
  /** [C5c §2] The order's shared columns — `[]` on every order that shares nothing. */
  shared: readonly SharedColumnDto[];
  ordinalOf: (dutyPointId: string) => number | undefined;
  /** [C6a] `awaitedIsLive(record)` — whether an empty command position may still read "awaited". */
  live: boolean;
}): React.ReactElement => {
  const duty = point.preview?.duty ?? null;
  /* ⚠️ [C5 §8a/§8b] Two figures, never merged — see the note at the render site. [C5b §3] Each is
     `null` at zero, from `pointOutstanding`, so the rule "absent, not 0" lives in one place. */
  const { stillToName: unfilled, yours } = pointOutstanding(point.preview);
  /*
   * ⚠️ [C4a — premise (e)] THE CARD'S OPEN STATE IS TRACKED, SO THE ROSTER GATE CAN BE A GATE AGAIN.
   *
   * `274bbe4` changed `DutyRoster enabled={open}` to `enabled={true}`, and the reason is visible
   * here: `open` was only the INITIAL answer (`expandedIndex`), so an operator expanding a collapsed
   * card toggled the DOM and nothing told React — the roster never loaded. `true` fixed that by
   * reading every card's sheet on mount, which is up to 24 requests on the heaviest screen in the
   * product and turned `ArrangementView.test.tsx`'s "reads NO roster while every card is collapsed"
   * red. `onToggle` reports the element's own `open`, so the read follows what the operator did.
   */
  const [isOpen, setIsOpen] = useState(open);

  return (
    <details
      open={isOpen}
      onToggle={(event) => {
        setIsOpen(event.currentTarget.open);
      }}
      className="rounded-lg border border-border bg-card"
      data-testid={`view-point-${point.id}`}
    >
      {/*
        ⚠️ [C3b §3.2] EVERYTHING THAT DESCRIBES THE CARD IS ON THE LEFT; ONLY THE ACTION IS ON THE
        RIGHT. The date and time used to sit in their own `shrink-0` span AFTER the `flex-1`
        title, which pushed them to the right edge and left the header reading as three
        disconnected fragments. They are now inside the identity group, so a card reads
        "DP1 · Esplanade · 15 Aug 2026 · 06:00–14:00" left to right, and `Name officers` is the
        one thing at the far edge — `flex-1` on the identity group is what puts it there, with no
        `justify-between` involved.

        ⚠️ THE ORDINAL IS `displayOrder + 1`, WHICH IS AN IDENTITY AND NOT THIS ARRAY'S POSITION.
        §7's rule bites on a derived KEY, and this is a derived LABEL — but the honest source is
        the same either way: `ArrangementPreviewModal` already renders `DP{displayOrder + 1}` and
        the printed sheet numbers from `orderSheetDutyPointSchema.ordinal`, so the number an
        operator reads here is the number on the paper. Deriving it from `.map`'s index would
        disagree with both the moment a card is filtered or reordered on one surface only.
      */}
      <summary className="flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
        <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span
            className="shrink-0 text-xs font-medium text-muted-foreground"
            data-testid={`view-point-dp-${point.id}`}
          >
            {VIEW_COPY.dutyPointOrdinal(point.displayOrder + 1)}
          </span>
          {/*
            ⚠️ RENDERED ONLY WHEN IT SAYS SOMETHING THE ORDINAL DOES NOT. `dutyPointDisplayTitle`
            falls back to `Duty point ${ordinal}` for a card with no name and no place, so an
            unconditional join reads "DP1 · Duty point 1" — the same number twice, which is
            exactly what a label beside an ordinal must not do.
          */}
          {point.displayTitle === dutyPointDisplayTitle({ places: [] }, point.displayOrder + 1) ? null : (
            /* [PLACE-500] A place may run to 500 characters; the line still truncates, and hover shows it whole. */
            <span className="min-w-0 truncate text-sm font-medium text-heading" title={point.displayTitle}>
              {point.displayTitle}
            </span>
          )}
          {/*
            [C4a §4e] The zone describes the card, so it sits in the identity group — absent when unset.
            ⚠️ A truthiness test, not `=== null`: a response WITHOUT the key (a newer page against an
            older API mid-deploy) must render no zone rather than "Zone: undefined".
          */}
          {point.zone ? (
            <span className="text-xs text-muted-foreground" data-testid={`view-point-zone-${point.id}`}>
              {PREVIEW_COPY.zone(point.zone)}
            </span>
          ) : null}
          {/*
            ⚠️ [C5c §4] The sector beside it, by the same truthiness test — and WRAPPED, never
            truncated. It has no length limit and this page IS the record, so there is nowhere further
            to read the rest; `min-w-0` lets a long one shrink to the row and `break-words` breaks it.
          */}
          {point.sector ? (
            <span
              className="min-w-0 break-words text-xs text-muted-foreground"
              data-testid={`view-point-sector-${point.id}`}
            >
              {PREVIEW_COPY.sector(point.sector)}
            </span>
          ) : null}
          {duty === null ? null : (
            <span className="text-xs text-muted-foreground" data-testid={`view-point-when-${point.id}`}>
              {readableDate(duty.dutyDate) ?? duty.dutyDate} · {duty.timeLabel}
              {point.deploymentCount > 1 ? ` · ${VIEW_COPY.ofDuties(point.deploymentCount)}` : ''}
            </span>
          )}
        </span>
        {/*
          ─────────────────────────────────────────────────────────────────────────────
           ⚠️ [C5 §8a/§8b] TWO FIGURES IN THE ROW'S OWN WHITE SPACE, AND NEVER ONE INDICATOR.

           The row said nothing at all about its state. It says two things now, and they are
           deliberately two: what the duty point still LACKS (objective, identical for every
           viewer) and what THIS VIEWER still owes (personal, absent when they owe nothing).

           ⚠️ [UX1 §3] SETTLED THIS AND THIS PROJECT HAS RE-LITIGATED IT THREE TIMES: a division
           that DELEGATED everything has discharged its obligation while the duty point remains
           unstaffed. Merging the two would lie to one of them — to the division that it still
           owes work it handed on, or to a commander that the duty is covered.

           ⚠️ THE PERSONAL ONE IS ABSENT, NOT ZERO. `yoursUnfilled` is `null` for a super admin
           (no scope of their own) and `0` for a division that owes nothing; neither renders. A
           `0 yours` chip would be a row of noise on every screen it appears on.

           ⚠️ AND IT SITS BEFORE THE `Name officers` LINK, in the gap `flex-1` already opens.
           There is no `justify-between` here, so nothing had to move to make room.
          ─────────────────────────────────────────────────────────────────────────────
        */}
        {/* ⚠️ `preview` is OPTIONAL on `dutyPointSchema` — it is what `?view=preview` adds — so a
            record-view payload renders neither figure rather than a zero. */}
        {unfilled !== null ? (
          <span className="shrink-0 text-xs text-muted-foreground" data-testid={`view-point-unfilled-${point.id}`}>
            {VIEW_COPY.stillToName(unfilled)}
          </span>
        ) : null}
        {yours !== null ? (
          <span className="shrink-0 text-xs font-medium text-heading" data-testid={`view-point-yours-${point.id}`}>
            {VIEW_COPY.yoursToName(yours)}
          </span>
        ) : null}
        {duty === null ? null : (
          <Link
            to={`/duties/${duty.deploymentId}`}
            className="shrink-0 text-xs text-link"
            data-testid={`view-name-officers-${point.id}`}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {VIEW_COPY.nameOfficers}
          </Link>
        )}
      </summary>

      <div className="flex flex-col gap-3 border-t border-border px-4 py-3">
        {/* ⚠️ §4e — COMMAND FIRST, WITH PHONES, THEN THE FORCE GRID BENEATH. */}
        {point.commandEntries.length > 0 ? <CommandContacts point={point} live={live} /> : null}

        {/*
          ⚠️ [C3b §2] THE FACE VIEW, NOT THE TABLE. `ForceMatrix` answered "how many" with a grid
          of `n of m` cells; this screen is where somebody asks WHO, and the fill view puts the
          names on chips with the rank and the phone behind a hover card. Same component the fill
          screen uses, every write affordance withheld — see `DutyRoster`'s header.

          ⚠️ `enabled={open}` IS LOAD-BEARING. `MAX_DUTY_POINTS` is 24 and every accordion renders
          on mount, so an ungated sheet read would fire twenty-four requests the moment the page
          opened. A card that has never been expanded costs nothing.
        */}
        {/* [C5c §2] A column asked for once across the order — the preview modal's own words. */}
        <SharedColumnLines shared={shared} dutyPointId={point.id} ordinalOf={ordinalOf} testIdPrefix="view-shared" />

        {/*
          ⚠️ [C5c §2] A COVERED CARD WHOSE ONLY FORCE IS SHARED STILL READS ITS ROSTER — the fill sheet
          carries the covering officers as `sharedLines` — and is never told it "asks for no force".
        */}
        {duty === null ? (
          <p className="text-sm text-muted-foreground">{VIEW_COPY.noDuty}</p>
        ) : duty.posts.length === 0 && !coveredElsewhere(shared, point.id) ? (
          <p className="text-sm text-muted-foreground">{VIEW_COPY.noForce}</p>
        ) : (
          <DutyRoster dutyId={duty.deploymentId} enabled={isOpen} />
        )}
        {/*
          ⚠️ [P7A §4] THE SHOWN DUTY'S SHORT-FALL NOTES, UNDER ITS ROSTER — `Still to come` apart from
          `Closed short`, with the reason a division gave. They were on this payload all along (the same
          `?view=preview` read the modal renders them from) and this page rendered none, so a division
          that had replied and one that could still be chased read alike here. The collapsed header's
          figures do not move: `N still to name` is the card's, over every night ([C5 §8]).
        */}
        {duty === null ? null : <ShortfallNotes notes={duty.shortfalls} testId={`view-shortfalls-${point.id}`} />}
        {/* ⚠️ [C5c §5a] The shown duty's own line, under its roster — `null` until it has started. */}
        {duty?.presence ? (
          <p className="text-xs text-muted-foreground" data-testid={`view-point-attendance-${point.id}`}>
            {VIEW_COPY.attendance}: {presenceLine(duty.presence)}
          </p>
        ) : null}
      </div>
    </details>
  );
};

const ArrangementViewPage = (): React.ReactElement => {
  const { arrangementId = '' } = useParams();
  /*
   * ⚠️ [TYPED-BACK] THE LIST THIS ORDER WAS OPENED FROM, AS THE ADDRESS NAMES IT — `lib/listOrigin.ts`
   * says what it accepts (a registered typed list's own address, nothing else). Read here because a hook
   * may not wait for the record; it is checked against the record's TYPE below (`origin`), before
   * anything reads it.
   *
   * ⚠️ THE ROUTER'S LOCATION IS THE TRUE URL HERE, which it is not on a list: `?from=` arrives by a
   * NAVIGATION into this page, and nothing on this page writes the URL through nuqs ([ANALYTICS §5]'s
   * shallow write is what `listOriginFor` exists to get around).
   *
   * ⚠️ IT IS NOT CARRIED OFF THE ORDER — `Name officers` (`/duties/…`), the printed order (a new tab of
   * its own) and the template link each open another screen with its own way back, and an origin there
   * would be a parameter nothing reads.
   */
  const listed = listOriginOf(useLocation().search);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const { officer } = useAuth();
  const { nodalsMayAuthor } = useArrangementAuthoringSetting();

  const canDelete = officer ? can(officer.accessRole, 'arrangement.delete') : false;
  const canPublish = officer ? can(officer.accessRole, 'arrangement.publish') : false;
  const canWrite = officer ? can(officer.accessRole, 'arrangement.write') : false;
  const mayAuthor = officer
    ? mayAuthorArrangement(officer.accessRole, actingOrgUnitTypeOf(officer), nodalsMayAuthor)
    : false;

  const arrangement = useQuery({
    queryKey: queryKeys.arrangements.preview(arrangementId),
    queryFn: ({ signal }) =>
      api.get<ArrangementDto>(`/arrangements/${arrangementId}`, { query: { view: 'preview' }, signal }),
  });

  const record = arrangement.data;
  const archived = Boolean(record?.archivedAt);
  const isDraft = record?.status === 'DRAFT';

  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
  const archiving = useArchiveArrangement({ onDone: invalidate });

  /*
   * ⚠️ BOTH IMPACT READS ARE `enabled`-GATED, so neither is paid where it cannot apply — the
   * tier only for an unarchived record, the blockers only for a draft.
   */
  const impact = useQuery({
    queryKey: [...queryKeys.arrangements.detail(arrangementId), 'delete-impact'],
    queryFn: ({ signal }) => api.get<DeleteImpactDto>(`/arrangements/${arrangementId}/delete-impact`, { signal }),
    enabled: Boolean(record) && !archived,
  });

  const publishPreview = useQuery({
    queryKey: queryKeys.arrangements.publishPreview(arrangementId),
    queryFn: ({ signal }) => api.get<PublishPreviewDto>(`/arrangements/${arrangementId}/publish-preview`, { signal }),
    enabled: Boolean(record) && isDraft,
  });

  const publish = useMutation({
    mutationFn: () =>
      api.post<ArrangementDto>(`/arrangements/${arrangementId}/publish`, { intent: 'REQUEST_DIVISIONS' }),
    onSuccess: () => {
      void message.success('Published');
      invalidate();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  if (arrangement.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }

  if (!record) return <EmptyState title="That order could not be loaded." />;

  /*
   * ⚠️ [TYPED-BACK] THE ORIGIN, KEPT ONLY WHEN IT IS A LIST THIS ORDER IS ON (`originForOrder`). A typed
   * list holds only its own type, so every link the UI builds agrees; a hand-made `?from=` naming a typed
   * list on an UNTYPED order is ignored rather than sending it "back" to a list that never shows it. It
   * decides the back link, and it rides the two doors to the builder (`withRecordOrigin`, which adds
   * `?via=record`), so the builder returns HERE — [D4.3] — and this page returns to the list.
   */
  const origin = originForOrder(listed, record.type);
  /* [DP2026] The back link's destination: the origin, else where the order is listed (`homeListOf`). */
  const back = origin ?? homeListOf(record);

  /* [C5b §3] The pill and the strip, from `lib/statusPresentation.ts` — see each render site. */
  const statusWord = orderStatusWord(record);
  const filled = fillSummary(record);

  /*
   * ⚠️ `publishReady` RATHER THAN `blockers.length === 0`, BECAUSE AN UNRESOLVED QUERY IS NOT AN
   * EMPTY ONE. `?? []` makes "still fetching" indistinguishable from "nothing in the way", so the
   * control rendered PRIMARY and `data-state="ready"` on first paint and then flipped to blocked
   * a moment later — hide-then-show read backwards ([D4.5 §2a]), on the one button that issues
   * an order to forty officers.
   */
  const blockers = publishPreview.data?.blockers ?? [];
  const publishReady = publishPreview.data !== undefined && publishPreview.data.blockers.length === 0;
  const tier = impact.data?.tier;
  /*
   * ⚠️ A DRAFT SHORT-CIRCUITS THE TIER GATE, MIRRORING THE SERVER. `assertMayArchive`'s own
   * `origin === 'DRAFT'` arm: discarding a draft nobody was ever told about is not the act
   * `mayArchiveTier` narrows, so asking it here would refuse the one operator it was widened for.
   */
  const mayArchive =
    officer !== undefined &&
    officer !== null &&
    tier !== undefined &&
    (isDraft || mayArchiveTier(officer.accessRole, actingOrgUnitTypeOf(officer), tier));

  /*
   * ⚠️ §4a — THE ARCHIVE SLOT, AND `undefined` MEANS "NOT YET", NOT "NO". Hide-then-show
   * ([D4.5 §2a]): a destructive action offered and then withdrawn is worse than one offered a
   * beat late, so nothing renders until the tier has arrived.
   */
  const archiveSlot = archived ? (
    archiving.mayRestore(record) ? (
      <Tooltip title={ARCHIVE_COPY.restore}>
        <Button
          loading={archiving.isRestoring}
          data-testid="view-restore"
          onClick={() => {
            archiving.restore(record);
          }}
        >
          {ARCHIVE_COPY.menuRestore}
        </Button>
      </Tooltip>
    ) : (
      <span className="text-xs text-muted-foreground" data-testid={VIEW_COPY.archiveUnavailable}>
        {ARCHIVE_COPY.restoreNotYours}
      </span>
    )
  ) : tier === undefined ? null : tier === 'BLOCKED' ? (
    <span className="text-xs text-muted-foreground" data-testid={VIEW_COPY.archiveUnavailable}>
      {impact.data?.hasOpenedWindow === true ? ARCHIVE_COPY.archiveBlocked : ARCHIVE_COPY.archiveBlockedEvent}
    </span>
  ) : !canDelete || !mayArchive ? (
    <span className="text-xs text-muted-foreground" data-testid={VIEW_COPY.archiveUnavailable}>
      {/*
        ⚠️ TWO DIFFERENT REFUSALS, AND ONE SENTENCE CANNOT SAY BOTH. This branch collapses "your
        role writes nothing at all" and "this tier is not yours", then picked the words from the
        TIER — so a `NODAL_READ` or `NODAL_MONITOR`, who reaches this page on `arrangement.read`
        alone, was told the archive was somebody else's to file rather than that they hold no
        write capability. The app-wide read-only sentence is the true one for them.
      */}
      {!canDelete
        ? READ_ONLY_TOOLTIP
        : tier === 'FILING'
          ? ARCHIVE_COPY.archiveNotYours
          : ARCHIVE_COPY.archiveNotYoursLive}
    </span>
  ) : (
    <Button
      danger={tier !== 'FILING'}
      loading={archiving.isArchiving}
      data-testid="view-archive"
      onClick={() => {
        void archiving.confirmArchive(record);
      }}
    >
      {isDraft ? ARCHIVE_COPY.menuDiscard : tier === 'FILING' ? ARCHIVE_COPY.archiveFileOk : ARCHIVE_COPY.menuArchive}
    </Button>
  );

  /*
   * ⚠️ [amended C3c.2] ONE DUTY POINT IS EXPANDED; SEVERAL EXPAND NONE.
   *
   * It was `index === Math.max(openIndex, 0)`, so exactly one card was ALWAYS open — the first
   * with a resolved duty, falling back to card zero. On a twenty-four-point order that is one
   * card's force grid unfurled at the top and twenty-three collapsed beneath it, which reads as
   * "this one is special" when nothing about it is; and it pushes the rest below the fold on the
   * screen an operator opens to scan the whole order.
   *
   * ⚠️ THE ONE-CARD CASE IS THE OPPOSITE PROBLEM, WHICH IS WHY IT IS NOT SIMPLY "COLLAPSE ALL":
   * a single collapsed card is a page whose entire content is hidden behind one click, for no
   * choice the operator has to make.
   */
  const expandedIndex = record.dutyPoints.length === 1 ? 0 : -1;

  return (
    <div className="flex flex-col gap-4" data-testid="arrangement-view">
      <PageHeader
        icon={CalendarClock}
        /*
         * [P7B.1 §4] A typed order's way back is its own list, which is no longer a tab of Manage Arrangements.
         * ⚠️ [amended TYPED-BACK] AND THE LIST IT WAS OPENED FROM COMES FIRST — its page, sort and filters
         * included, so going back does not land on page one. A link, never `history.back()` ([D4.3]): the
         * origin survives a reload and a pasted address. ⚠️ [amended TYPE-FILTER] Without an origin it is Manage
         * Arrangements whatever the type — that list shows every order now — so [P7B.1 §4]'s typed branch is gone.
         */
        /*
         * ⚠️ [DP2026] …EXCEPT A DRAFT MADE FROM ITS OWN TYPE'S BOOKLET, which Manage Arrangements does not list
         * until it is published: with no origin it leads back to its type's list (`homeListOf`).
         */
        parent={back ? { label: back.label, to: back.to } : { label: VIEW_COPY.parent, to: '/arrangements' }}
        title={`${record.paNumber ?? record.reference} · ${record.displayTitle}`}
        actions={
          <>
            {/*
              ⚠️ §4a — PUBLISH BELONGS ONLY ON A DRAFT, AND NEVER ON AN **ARCHIVED** ONE.
              `archiveArrangement` refuses a record already archived, so offering it here is
              §4a's own rule broken — an action rendered that will refuse — on a screen whose
              other control is `Restore`. A discarded draft beside an enabled Publish is also
              simply confusing about which state the order is in.
            */}
            {!archived && isDraft && canPublish && mayAuthor ? (
              <Button
                type={publishReady ? 'primary' : 'default'}
                loading={publish.isPending}
                data-testid="view-publish"
                data-state={publishReady ? 'ready' : 'blocked'}
                onClick={() => {
                  if (blockers.length > 0) {
                    /* [TYPED-BACK] A record page's door: the builder returns to this record, which keeps
                       the way back to the list this order came from. */
                    void navigate(withRecordOrigin(`/arrangements/${arrangementId}/edit`, origin));
                    return;
                  }
                  modal.confirm({
                    title: 'Publish this arrangement?',
                    content: 'The dates and places cannot be changed afterwards.',
                    okText: VIEW_COPY.publish,
                    onOk: () => {
                      publish.mutate();
                    },
                  });
                }}
              >
                {VIEW_COPY.publish}
              </Button>
            ) : null}

            {/*
             * ⚠️ §4a — GATED ON WHAT THE **SERVER** REFUSES, WHICH IS NOT WHAT THE ROUTE DOES.
             *
             * ⚠️ **[amended C3c §9c] THE ROUTE IS GATED NOW, AND THIS COMMENT SAID IT WAS NOT.**
             * `/arrangements/:id/edit` carries `RequireCapability('arrangement.write')` **and**
             * `RequireDivision` — the same pair `/arrangements/new` has had since [UX1 §8]. Before
             * §9c it carried `arrangement.read` and nothing else, which is what the old text
             * described and what made the "known gap" below true.
             *
             * This button stays an OFFER gate all the same: it mirrors what the SERVER refuses
             * (`assertMayAuthorArrangement` on all five authoring verbs), so an operator learns
             * from the screen rather than from a 403 after typing.
             *
             * ⚠️ AND `mayAuthor` IS HALF OF IT. [C2a §3] made authoring five verbs behind one
             * predicate, and a nodal the setting refuses would otherwise reach a form whose every
             * save is refused. It is DISABLED rather than absent because Edit is the action an
             * operator looks for and an absent one reads as a broken page; the tooltip says which
             * of the three refusals applies.
             *
             * ⚠️ **[amended C3c §9c] THAT GAP IS CLOSED AND THE RECORD OF IT IS KEPT.** It read:
             * "a nodal who holds `arrangement.write` while the authoring setting is off can still
             * reach the form by URL and type into it, and every save then 403s". `RequireDivision`
             * on the route is what closes it, and it needed no new mechanism — that guard already
             * WAITS on the setting rather than deciding early, because `<Navigate replace>` is not
             * undoable by a later answer. `routeGuards.test.tsx` asserts it BY URL.
             *
             * ⚠️ IT IS NOT GATED ON `isDraft`. A published order is editable — [D4.4] built
             * un-publish precisely so it could be — and the builder itself decides what a
             * published record may change.
             */}
            {/*
              ⚠️ AND AN ARCHIVED ORDER IS NOT EDITABLE. The page is deliberately built to OPEN
              one — that is where Restore lives — so the archive flag has to reach every control
              on it rather than only the archive slot. §4a again: `updateArrangement` refuses,
              so the button must not be offered.
            */}
            <Tooltip
              title={
                archived
                  ? VIEW_COPY.editArchived
                  : canWrite
                    ? mayAuthor
                      ? undefined
                      : ARRANGEMENTS_COPY.mayNotRaise
                    : READ_ONLY_TOOLTIP
              }
            >
              <Button
                disabled={archived || !canWrite || !mayAuthor}
                data-testid="view-edit"
                /* [TYPED-BACK] The record page's door — `?via=record`, so the builder comes back here. */
                onClick={() => void navigate(withRecordOrigin(`/arrangements/${arrangementId}/edit`, origin))}
              >
                {VIEW_COPY.edit}
              </Button>
            </Tooltip>

            {/*
              ⚠️ [amended C3a §4] `Take back to draft` — RESTORED, BECAUSE §4 HID ITS ONLY DOOR.
              §4b named exactly two actions to hide and `SURFACES.arrangementRecordPage` took a
              THIRD with them, silently: [D4.4] built `PUBLISHED → DRAFT` on a client instruction
              that reversed [D4.3], and the control lived only on the page this one replaces.
              It renders nothing unless the record can actually be taken back.
            */}
            <UnpublishAction
              arrangementId={arrangementId}
              status={record.status}
              archived={archived}
              canPublish={canPublish}
              onDone={invalidate}
            />

            {/*
              ⚠️ [P7B §6b] THE `master_superadmin` OVERRIDE — offered ONLY where the ordinary act above is
              refused: an issued order whose archive tier is BLOCKED (a duty has started, one is
              unfinished). The tier is this page's own `delete-impact` read, so deciding costs nothing;
              the dialog's own impact read fires only when it opens. Renders nothing for anybody else.
            */}
            <RevertRunningAction
              arrangementId={arrangementId}
              status={record.status}
              archived={archived}
              tier={tier}
              onDone={invalidate}
            />

            {/*
              ⚠️ [C3b §4] EVERY PDF ON THIS ORDER, ONE CLICK FROM THE RECORD. It renders NOTHING
              when the order carries no files, so the header does not grow a control that opens
              onto an empty list — see the component's own header for why that is hide-then-show
              rather than a refusal. Its `Dropdown` and its `Modal` live in that file, so this
              page's census row does not move.
            */}
            <AttachmentsMenu arrangementId={arrangementId} />

            {/* ⚠️ A REAL `<a>` — `NameCell`'s rule. An operator printing wants a second tab. */}
            <Button
              href={`/arrangements/${arrangementId}/print`}
              target="_blank"
              rel="noreferrer"
              data-testid="print-order"
            >
              {VIEW_COPY.print}
            </Button>

            {archiveSlot}

            {/* [P7B §6c] A reverted draft, for good — the same holder, and two existing acts in sequence.
                [TYPED-BACK] Its `type` is where it lands with no origin: a typed order's own list. */}
            <DeleteRevertedDraftAction
              arrangementId={arrangementId}
              type={record.type}
              home={homeListOf(record)}
              isDraft={isDraft}
              archived={archived}
              reverted={record.revertedToDraft !== undefined}
            />
          </>
        }
      />

      {/*
        ⚠️ §4c — BRIEF PA INFORMATION, HOW MUCH IS FILLED, AND WHICH DIVISIONS. NOTHING ELSE.
        No extra buttons and no extra counts: the client's stated success metric is simplicity,
        and this page is being rebuilt because the old one lost it.
      */}
      <div
        className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border bg-card px-4 py-3 text-sm"
        data-testid="view-summary"
      >
        {/*
          ⚠️ THE PILL SAYS WHETHER THE ORDER IS IN CIRCULATION, AND IT READ ONLY `status`.
          [LIFECYCLE]'s rule is that archive is an AXIS rather than a status member — no enum
          contains `ARCHIVED` — so a renderer that shows only `status` shows an archived DRAFT as
          `Draft` and an archived published order as `Published`. Both the list and the old record
          page branch here; this page did not, so its ONE identity control was the one that could
          not say the order had been withdrawn.
        */}
        {/* ⚠️ [C5b §3] `orderStatusWord` — the one reader of the archive axis on every surface. */}
        <StatusPill tone={statusWord.tone}>{statusWord.label}</StatusPill>
        {/* [P7A §2] Who raised it — `HQ` · `DRO` · `Unit`, beside the state; nothing when `null`. */}
        <RaiserPill level={record.raisedAtLevel} />
        {/*
          ⚠️ [P7B §5] A TYPED ORDER SAYS SO — the type's chip, and where it came from. Both keys are
          OPTIONAL on the DTO and absent on every untyped order, so an untyped strip is exactly what it
          was. The template link renders only for a template author (the pages are theirs); for
          everyone else the same words are text, never a link to a page that would refuse them.
        */}
        {arrangementTypeLabel(record.type) === null ? null : (
          <span
            className="whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-xs text-foreground"
            data-testid="view-type"
          >
            {arrangementTypeLabel(record.type)}
          </span>
        )}
        {record.templateSource === undefined ? null : mayUseTemplates(officer) ? (
          <Link
            to={templateHref(record.templateSource.setKey, record.templateSource.templateKey)}
            className="text-xs text-link"
            data-testid="view-template-source"
          >
            {templateSourceLabel(record.templateSource)}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid="view-template-source">
            {templateSourceLabel(record.templateSource)}
          </span>
        )}
        <span className="text-muted-foreground">
          {VIEW_COPY.orderDate} <span className="text-foreground">{readableDate(record.paDate) ?? record.paDate}</span>
        </span>
        {record.firstDutyDate === null ? null : (
          <span className="text-muted-foreground">
            {VIEW_COPY.dutyDates}{' '}
            <span className="text-foreground">
              {readableDate(record.firstDutyDate)}
              {record.lastDutyDate && record.lastDutyDate !== record.firstDutyDate
                ? ` – ${readableDate(record.lastDutyDate) ?? ''}`
                : ''}
            </span>
          </span>
        )}
        {/*
          ⚠️ THE FILL FIGURE IS THE RECORD'S OWN, toned by its own `fillState`. No arithmetic.

          ⚠️ [C5 §3b] AND THE WORD COMES FROM `fillStateLabel`, NOT FROM THE MAP DIRECTLY. This
          line is where a client read `Filled: 0 of 12 · Complete` on a live order: every
          division had closed its share short, which IS complete by decision. It reads
          `Closed short` now — the state is unchanged and the sentence is true.

          ⚠️ [amended C5b §3] AND IT IS `fillSummary`, THE LIST'S OWN CELL — this strip said
          `19 of 19` where the list said `19 of 19 named` (D10), and the pill beside it said
          `Partially Filled` (D1). Both now come from `lib/statusPresentation.ts`.
        */}
        <StatusDot data-testid="view-fill" tone={filled.tone} label={`${VIEW_COPY.filled}: ${filled.label}`} />
        <span className="text-muted-foreground" data-testid="view-divisions">
          {VIEW_COPY.divisions}{' '}
          <span className="text-foreground">
            {record.divisions.map((division) => division.shortCode).join(', ') || VIEW_COPY.noDivisions}
          </span>
        </span>
        {/*
          ⚠️ [C5c §5a] THE ORDER'S ATTENDANCE, IN WORDS, AND ONLY ONCE A DUTY HAS STARTED — the
          preview's own line (`presenceLine`), read off the same `?view=preview` payload, so the two
          screens cannot word one tally differently. A short labelled fact, like its neighbours.
        */}
        {record.presence ? (
          <span className="text-muted-foreground" data-testid="view-attendance">
            {VIEW_COPY.attendance}: <span className="text-foreground">{presenceLine(record.presence)}</span>
          </span>
        ) : null}
      </div>

      {/*
        ⚠️ [C3c.2] THE DESCRIPTION, AND **ONLY WHEN THERE IS ONE**.

        It is the one thing an operator types about an order that this page never showed, so a
        note left for whoever picks the order up was written into a field nobody could read back.

        ⚠️ A SIBLING BELOW THE SUMMARY, NEVER A MEMBER OF IT. That row is
        `flex flex-wrap items-center` and every child is one short labelled fact; a paragraph
        inside it would be wrapped as though it were another chip, and a long one would push the
        status pill and the fill dot onto their own lines.

        ⚠️ AND NOTHING IS RENDERED WHEN IT IS ABSENT — no label, no empty card. `nullish` on the
        schema means most orders carry none, and an empty bordered box on every one of them is
        furniture. `?.trim()` because a description of spaces is not a description.
      */}
      {/*
        ⚠️ [P7B §6b] A RUNNING ORDER TAKEN BACK TO DRAFT SAYS SO, WHILE IT IS A DRAFT — when, and the
        stated reason. "a super admin", never a name: who it was is on the audit row. Publishing clears
        the key, so the banner goes with it.
      */}
      {record.revertedToDraft !== undefined && isDraft ? (
        <Alert
          type="warning"
          showIcon
          data-testid="view-reverted"
          message={VIEW_COPY.reverted(istDateTime(record.revertedToDraft.at), record.revertedToDraft.reason)}
        />
      ) : null}

      {(record.description ?? '').trim() === '' ? null : (
        <p
          className="whitespace-pre-wrap rounded-lg border border-border bg-card px-4 py-3 text-sm text-foreground"
          data-testid="view-description"
        >
          {record.description}
        </p>
      )}

      {/*
        ⚠️ §4d — ONE ACCORDION PER DUTY POINT, EVEN WHERE IT IS SPLIT ACROSS DIVISIONS. The split
        shows INSIDE the grid: `ForceMatrix` gives a delegated `CD · Tala` and `CD · Bowbazar`
        their own rows already ([C1h §4h]), which is why this needs no second grouping of its own.
      */}
      {record.dutyPoints.length === 0 ? (
        <EmptyState title={VIEW_COPY.noPoints} />
      ) : (
        <div className="flex flex-col gap-3" data-testid="view-points">
          {record.dutyPoints.map((point, index) => (
            <DutyPointCard
              key={point.id}
              point={point}
              open={index === expandedIndex}
              /* ⚠️ `?? []` — a cached response from before the key existed. */
              shared={record.sharedColumns ?? []}
              live={awaitedIsLive(record)}
              ordinalOf={(id) => {
                const found = record.dutyPoints.find((row) => row.id === id);
                return found ? found.displayOrder + 1 : undefined;
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ArrangementViewPage;
